
import {Component, Prop, Vue} from "vue-property-decorator";
import DataFieldCache from "../../../models/data.field.cache";
import DataFieldBar from "@/components/study/datafields/base/DataFieldBar.vue";
import {
  currentUserModule,
  dataFieldsEditModule,
  instrumentModule,
  studyPatientModule,
  synQuestInterviewModule
} from "@/store"
import {formatDate} from "@/auxillary/date-format";
import Hints from "@/components/study/datafields/base/Hints.vue";
import {MdbsCore} from "@/api/tsclient";
import SynQuestSystemShortInterviewDto = MdbsCore.SynQuestSystemShortInterviewDto;

@Component({
  name: 'SynQuestData',
  components: {
    DataFieldBar,
    Hints
  },
  filters: {
    date: function (value: Date | number | string | undefined) {
      return formatDate(value)
    }
  }
})
export default class SynQuestData extends Vue {

  @Prop({required: true})
  private dataField: DataFieldCache

  errorMessage: string = "";

  mounted() {
    const instrumentId = instrumentModule.instrument?.id ?? "";
    synQuestInterviewModule.loadByInstrumentId(instrumentId);
  }

  get displayName() {
    if (!this.dataField.displayName) return ""
    return this.dataField.displayName;
  }

  get field() {
    return this.dataField;
  }

  get isVisible() {
    return this.field.isVisible && !this.field.isNewField;
  }

  get isReadonly() {
    return !this.field.isEditable;
  }

  get baseUrl() {
    return this.field.definition.baseUrl ?? "";
  }

  get synQuestInterview() {
    return synQuestInterviewModule.interview;
  }

  get hasSynQuestInterview() {
    return !!(this.synQuestInterview?.id);
  }

  get hasSynQuestInterviewStarted() {
    return this.hasSynQuestInterview &&
        (this.synQuestInterview.questionnaires?.some(q => q.startDate !== undefined) ?? false);
  }

  get isPhysicianMode() {
    return dataFieldsEditModule.dataFields.some(d => d.identifier == "interviewType" && d.values?.value == "ePRO" || d.values?.value == "tablet")
  }

  get isSynQuestInterviewCompleted() {
    return this.hasSynQuestInterview &&
        (this.synQuestInterview.questionnaires?.every(q => q.isCompleted) ?? true);
  }

  get isSynQuestInterviewLocked() {
    return this.hasSynQuestInterview &&
        (this.synQuestInterview.isLocked ?? true);
  }

  filterInterviewQuestionnaires(interview: SynQuestSystemShortInterviewDto) {
    const deactivatedQuestionnaires = interview.questionnaires?.filter(q => q.isDeactivated) ?? [];
    const filteredQuestionnaires = interview.questionnaires?.filter(q => !q.isDeactivated) ?? [];

    for (const deactivatedQuestionnaire of deactivatedQuestionnaires) {
      if (filteredQuestionnaires.some(q => q.definitionId == deactivatedQuestionnaire.definitionId))
        continue;
      filteredQuestionnaires.push(deactivatedQuestionnaire)
    }
    return filteredQuestionnaires.sort((p1, p2) => (p1.displayName ?? "").localeCompare(p2.displayName ?? ""));
  }

  async editInterviewAsUser() {
    if (!await this.synQuestLoginAsUser()) {
      return;
    }

    const interviewId = this.synQuestInterview.id;

    this.synQuestRedirect(
        synQuestInterviewModule.userLogin?.baseUrl,
        synQuestInterviewModule.userLogin?.token?.refreshToken,
        `/userstartinterview/${interviewId}`);
  }

  async openPatientInterviews() {
    if (!await this.synQuestLoginAsUser()) {
      return;
    }

    const patientId = studyPatientModule.studyPatient.patientId ?? "";

    this.synQuestRedirect(
        synQuestInterviewModule.userLogin?.baseUrl,
        synQuestInterviewModule.userLogin?.token?.refreshToken,
        `/patient/${patientId}/interviews`);
  }

  async openMonitor() {
    if (!await this.synQuestLoginAsUser()) {
      return;
    }

    const interviewId = this.synQuestInterview.id;

    this.synQuestRedirect(
        synQuestInterviewModule.userLogin?.baseUrl,
        synQuestInterviewModule.userLogin?.token?.refreshToken,
        `/monitoring/${interviewId}`);
  }

  private async synQuestLoginAsUser() {
    const userId = currentUserModule.user?.id || "";
    const userName = currentUserModule.user?.userName || "";

    await synQuestInterviewModule.loginUser({
      userId: userId,
      userName: userName
    })

    return synQuestInterviewModule.isUserLoggedIn;
  }

  private synQuestRedirect(
      baseUrl: string | undefined,
      refreshToken: string | undefined,
      route: string): boolean {
    if (baseUrl == undefined || refreshToken == undefined) {
      return false;
    }

    const newTab = window.open(
        `${baseUrl}#/externalredirect?refreshToken=${refreshToken}&url=${route}`,
        "_blank"
    );
    newTab?.focus();

    return true;
  }

  destroyed() {
    synQuestInterviewModule.reset();
  }

}
